import React from 'react'
import '../sections/ai-mobile3.css'
import { cn } from '@/lib/utils'

/**
 * PhoneMockup (W11) — a pure-CSS phone frame for the Mobile section. The splash
 * screen shows the brand's three-bar ledger glyph (scaled up from the nav mark in
 * Marketing3Layout) + the {brand} wordmark on a soft teal-glow surface; after a
 * beat it cross-fades ONCE to a minimal "today" view (a couple of rows). A small
 * mono "Coming soon" chip is pinned on the frame corner — small, not a headline.
 *
 * Visible strings quoted VERBATIM from the copy doc's §W11 (PhoneMockup); the
 * role="img" label is the copy doc's §Widget aria-labels W11 line ({BRAND} → brand).
 * Figures are illustrative. No stage counts, no cadence, no PMS name.
 *
 * Reduced motion (or no matchMedia): the splash state stays static, with the chip
 * visible — the designed still, not a broken animation.
 */

const CORNER_CHIP = 'Coming soon'
const TODAY_HEADER = 'Today'
const TODAY_LINE = '8 rooms priced · pickup +3 tonight'

const CROSSFADE_DELAY_MS = 2200

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/** The stacked three-bar ledger glyph, scaled up for the splash (nav mark idiom). */
function LedgerGlyphLarge() {
  return (
    <span aria-hidden="true" className="flex flex-col items-start gap-1.5">
      <span className="h-1.5 w-14 rounded-full bg-teal-400 shadow-[0_0_14px_rgba(45,212,191,0.55)]" />
      <span className="h-1.5 w-10 rounded-full bg-slate-300" />
      <span className="h-1.5 w-6 rounded-full bg-slate-500" />
    </span>
  )
}

export default function PhoneMockup({ brand, className }: { brand: string; className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [reduced] = React.useState(prefersReducedMotion)
  // `crossed` drives the single one-shot splash → today transition.
  const [crossed, setCrossed] = React.useState(false)
  const [started, setStarted] = React.useState(false)

  // Trigger the cross-fade once the phone scrolls into view (play-once).
  React.useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced])

  React.useEffect(() => {
    if (reduced || !started) return
    const t = setTimeout(() => setCrossed(true), CROSSFADE_DELAY_MS)
    return () => clearTimeout(t)
  }, [started, reduced])

  return (
    <div
      ref={ref}
      className={cn('relative mx-auto w-[240px] sm:w-[264px]', className)}
      role="img"
      aria-label={`A phone showing the ${brand} today view, with a coming-soon chip in the corner.`}
    >
      {/* Phone frame — pure CSS: dark bezel, rounded, soft outer glow */}
      <div className="mkt3-frame relative aspect-[9/19] overflow-hidden rounded-[2.5rem] border-[7px] border-slate-800 bg-slate-950 shadow-2xl">
        {/* Notch */}
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-2.5 z-20 h-1.5 w-16 -translate-x-1/2 rounded-full bg-slate-800/90"
        />

        {/* Soft teal-glow surface behind both layers */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 70% at 50% 22%, rgba(13,148,136,0.28), transparent 62%), linear-gradient(180deg, #030712, #01040c)',
          }}
        />

        {/* Splash layer — brand glyph + wordmark on the teal glow */}
        <div
          className={cn(
            'mkt3-mob-layer mkt3-mob-splash absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 px-6',
            !reduced && crossed && 'mkt3-mob-out'
          )}
        >
          <span aria-hidden="true" className="mkt3-mob-breathe absolute h-24 w-24 rounded-full bg-teal-500/25 blur-2xl" />
          <div className="relative flex flex-col items-center gap-4">
            <LedgerGlyphLarge />
            <span className="mkt3-display text-2xl leading-none text-slate-50">{brand}</span>
          </div>
        </div>

        {/* Today layer — minimal today view, revealed after the cross-fade */}
        <div
          className={cn(
            'mkt3-mob-layer mkt3-mob-today absolute inset-0 z-10 flex flex-col px-4 pb-5 pt-11',
            (reduced ? false : crossed) && 'mkt3-mob-in'
          )}
          aria-hidden={reduced || !crossed ? 'true' : undefined}
        >
          {/* Status bar strip */}
          <div className="mb-4 flex items-center justify-between px-1">
            <div className="flex items-center gap-1.5">
              <span aria-hidden="true" className="flex flex-col gap-[2px]">
                <span className="h-[2px] w-3 rounded-full bg-teal-400" />
                <span className="h-[2px] w-2 rounded-full bg-slate-500" />
                <span className="h-[2px] w-1 rounded-full bg-slate-600" />
              </span>
              <span className="mkt3-display text-sm leading-none text-slate-100">{brand}</span>
            </div>
            <span
              aria-hidden="true"
              className="mkt3-live-dot h-1.5 w-1.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.9)]"
            />
          </div>

          {/* Today card */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-3.5 backdrop-blur">
            <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-teal-300/80">
              {TODAY_HEADER}
            </p>
            <p className="mt-2 text-[13px] font-medium leading-snug text-slate-100">{TODAY_LINE}</p>
          </div>

          {/* Two illustrative rows beneath — quiet, plain, no numbers claimed as fact */}
          <div className="mt-3 space-y-2">
            {[
              { room: 'Garden Double', tag: '£120' },
              { room: 'Standard Twin', tag: '£101' },
            ].map((row) => (
              <div
                key={row.room}
                className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2"
              >
                <span className="text-[12px] text-slate-300">{row.room}</span>
                <span className="font-mono text-[12px] font-semibold tabular-nums text-slate-100">{row.tag}</span>
              </div>
            ))}
          </div>

          <div className="mt-auto flex justify-center pt-3">
            <span aria-hidden="true" className="h-1 w-10 rounded-full bg-slate-700" />
          </div>
        </div>
      </div>

      {/* Corner chip — small, cornered "Coming soon", never a headline */}
      <span className="absolute -right-2 -top-2 z-30 inline-flex items-center gap-1.5 rounded-full border border-teal-400/30 bg-slate-900/90 px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-teal-300 shadow-lg backdrop-blur">
        <span aria-hidden="true" className="h-1 w-1 rounded-full bg-teal-400" />
        {CORNER_CHIP}
      </span>
    </div>
  )
}
